import Dagre from '@dagrejs/dagre';
import { type Node, type Edge } from '@xyflow/react';
import {
  DEFAULT_NODE_WIDTH,
  DEFAULT_NODE_HEIGHT,
  AUTO_LAYOUT_VERTICAL_GAP,
  AUTO_LAYOUT_HORIZONTAL_GAP,
} from '@/constants/workflow-editor';
import { CustomNode } from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import { isCanvasDecorationNode } from '@/pages/workflow/helper/graph-analysis';

export type LayoutDirection = 'TB' | 'LR';

export function getAutoLayoutPositions(
  nodes: Node[],
  edges: Edge[],
  direction: LayoutDirection = 'TB',
): Map<string, { x: number; y: number }> {
  // Grouped children are excluded too, not just the GroupNode container
  // itself — their position is relative to their parent group, so writing
  // an absolute Dagre-computed position onto them (while parentId stays
  // set) would misplace them. Auto-layout only rearranges top-level nodes;
  // grouped clusters stay put.
  const layoutNodes = nodes.filter(
    (node) => !isCanvasDecorationNode(node as CustomNode) && !node.parentId,
  );
  const layoutNodeIds = new Set(layoutNodes.map((node) => node.id));
  const layoutEdges = edges.filter(
    (edge) =>
      (edge as CustomEdge).edge_type !== ConnectionType.REPEAT &&
      (edge as CustomEdge).edge_type !== ConnectionType.MAP_COLLECT &&
      layoutNodeIds.has(edge.source) &&
      layoutNodeIds.has(edge.target),
  );

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === 'LR';

  g.setGraph({
    rankdir: direction,
    nodesep: isHorizontal
      ? AUTO_LAYOUT_VERTICAL_GAP
      : AUTO_LAYOUT_HORIZONTAL_GAP,
    ranksep: isHorizontal
      ? AUTO_LAYOUT_HORIZONTAL_GAP
      : AUTO_LAYOUT_VERTICAL_GAP,
    marginx: 40,
    marginy: 40,
  });

  for (const node of layoutNodes) {
    const width = node.measured?.width ?? DEFAULT_NODE_WIDTH;
    const height = node.measured?.height ?? DEFAULT_NODE_HEIGHT;
    g.setNode(node.id, { width, height });
  }

  for (const edge of layoutEdges) {
    g.setEdge(edge.source, edge.target);
  }

  Dagre.layout(g);

  const positions = new Map<string, { x: number; y: number }>();

  for (const node of layoutNodes) {
    const dagreNode = g.node(node.id);
    if (!dagreNode) continue;

    const width = node.measured?.width ?? DEFAULT_NODE_WIDTH;
    const height = node.measured?.height ?? DEFAULT_NODE_HEIGHT;

    positions.set(node.id, {
      x: dagreNode.x - width / 2,
      y: dagreNode.y - height / 2,
    });
  }

  return positions;
}
